import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Upload,
  FileText,
  Trash2,
  Loader2,
  CheckCircle2,
  Circle,
  XCircle,
  Download,
  Rocket,
  RotateCcw,
  Zap,
  Wrench,
  Database,
  Sparkles,
  Pencil,
  FlaskConical,
  Share2,
  ChevronRight,
  Info,
} from 'lucide-react';
import apiClient from '../api/client';
import { streamJobEvents, type SseEvent } from '../api/sse';
import { downloadGeneratedArtifact } from '../api/generate';
import { useToast } from '../components/ui/toastContext';

// 进行中的生成任务持久化键：页面刷新后从历史事件重建进度再接 SSE（P11）
const JOB_STORAGE_KEY = 'atelier:material-lane-job';

type TargetKind = '' | 'skill' | 'mcp' | 'kb';

interface DecisionInfo {
  kind: string;
  confidence: number;
  reasons: string[];
  override?: boolean;
}

interface LintInfo {
  passed: boolean;
  errors: string[];
  warnings: string[];
}

interface GenerateResult {
  artifact_id: string;
  kind: string;
  name: string;
  degraded?: boolean;
  decision?: DecisionInfo;
  decision_override?: boolean;
  lint?: LintInfo;
  files?: string[];
  zip_path?: string;
  warnings?: string[];
  version?: string;
}

// refine（B16）结果摘要
interface RefineSummary {
  version?: string;
  gap_analysis?: string;
  changes_applied?: number;
  changes_skipped?: number;
  warnings?: string[];
}

const KIND_LABELS: Record<string, string> = {
  skill: '技能（Skill）',
  mcp: 'MCP 工具',
  kb: '知识库（KB）',
  preset: '能力预设',
};

const STAGES = [
  { key: 'parsed', label: '解析材料' },
  { key: 'decided', label: '决策产物类型' },
  { key: 'generated', label: '生成产物' },
  { key: 'linted', label: '质量检查' },
  { key: 'registered', label: '登记产物' },
];

const REFINE_STAGES = [
  { key: 'refine_started', label: '分析缺口' },
  { key: 'refined', label: '应用增量修订' },
  { key: 'linted', label: '质量检查' },
  { key: 'registered', label: '登记新版本' },
];

const STEP_TITLES = ['材料上传', '产物类型', '生成进度', '产物预览'];

const ACCEPT = '.md,.txt,.docx,.pdf,.json,.yaml,.yml';

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const kindLabel = (kind: string) => KIND_LABELS[kind] || kind;

export default function MaterialLane() {
  const toast = useToast();
  const [step, setStep] = useState(1);
  const [files, setFiles] = useState<File[]>([]);
  const [targetKind, setTargetKind] = useState<TargetKind>('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [doneEvents, setDoneEvents] = useState<string[]>([]);
  const [parsedCount, setParsedCount] = useState(0);
  const [decision, setDecision] = useState<DecisionInfo | null>(null);
  const [lint, setLint] = useState<LintInfo | null>(null);
  const [result, setResult] = useState<GenerateResult | null>(null);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // refine（B16）：源任务 job_id + 完善状态
  const [jobId, setJobId] = useState<string | null>(null);
  const [refining, setRefining] = useState(false);
  const [refineInstruction, setRefineInstruction] = useState('');
  const [refineDoneEvents, setRefineDoneEvents] = useState<string[]>([]);
  const [refineSummary, setRefineSummary] = useState<RefineSummary | null>(null);
  const [refineError, setRefineError] = useState('');

  const handleEvent = (event: SseEvent) => {
    const data = (event.data || {}) as Record<string, unknown>;
    switch (event.type) {
      case 'parsed':
        setParsedCount((n) => n + 1);
        setDoneEvents((prev) => (prev.includes('parsed') ? prev : [...prev, 'parsed']));
        break;
      case 'decided':
        setDecision({
          kind: String(data.kind || ''),
          confidence: Number(data.confidence ?? 0),
          reasons: Array.isArray(data.reasons) ? (data.reasons as string[]) : [],
          override: Boolean(data.override),
        });
        setDoneEvents((prev) => [...prev, 'decided']);
        break;
      case 'generated':
        setDoneEvents((prev) => [...prev, 'generated']);
        break;
      case 'linted':
        setLint({
          passed: Boolean(data.passed),
          errors: Array.isArray(data.errors) ? (data.errors as string[]) : [],
          warnings: Array.isArray(data.warnings) ? (data.warnings as string[]) : [],
        });
        setDoneEvents((prev) => [...prev, 'linted']);
        break;
      case 'registered':
        setDoneEvents((prev) => [...prev, 'registered']);
        break;
      case 'done':
        // 任务终态：清除持久化键，刷新页面不再恢复
        sessionStorage.removeItem(JOB_STORAGE_KEY);
        if (data.status === 'succeeded' && data.result) {
          setResult(data.result as GenerateResult);
          setStep(4);
        } else {
          setError(String(data.error || '生成失败'));
        }
        break;
      default:
        break;
    }
  };

  // 订阅任务事件流：先拉历史事件重建进度（刷新不丢进度），再接 SSE 实时流
  const subscribeJob = (jobId: string) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    streamJobEvents(jobId, handleEvent, controller.signal).catch((e) => {
      if (e?.name === 'AbortError') return;
      setError(e?.message || '订阅任务进度失败');
    });
  };

  // refine（B16）事件流：refine_started → refined → linted → registered → done
  const handleRefineEvent = (event: SseEvent) => {
    const data = (event.data || {}) as Record<string, unknown>;
    switch (event.type) {
      case 'refine_started':
      case 'refined':
      case 'linted':
      case 'registered':
        setRefineDoneEvents((prev) => (prev.includes(event.type) ? prev : [...prev, event.type]));
        break;
      case 'done':
        if (data.status === 'succeeded' && data.result) {
          const refined = data.result as GenerateResult & RefineSummary;
          setRefineSummary(refined);
          // 合并回预览结果：保留决策信息，更新版本 / lint / 文件清单
          setResult((prev) =>
            prev
              ? {
                  ...prev,
                  version: refined.version ?? prev.version,
                  lint: refined.lint ?? prev.lint,
                  files: refined.files ?? prev.files,
                }
              : prev
          );
          toast.success('完善完成，产物已更新');
        } else {
          setRefineError(String(data.error || '完善失败'));
        }
        setRefining(false);
        break;
      default:
        break;
    }
  };

  const refine = async () => {
    if (!jobId || refining || !refineInstruction.trim()) return;
    setRefining(true);
    setRefineError('');
    setRefineSummary(null);
    setRefineDoneEvents([]);
    try {
      const res = await apiClient.post(`/generate/${jobId}/refine`, { instruction: refineInstruction.trim() });
      const refineJobId = res.data.job_id as string;
      streamJobEvents(refineJobId, handleRefineEvent, abortRef.current?.signal).catch((e) => {
        if (e?.name === 'AbortError') return;
        setRefineError(e?.message || '订阅完善进度失败');
        setRefining(false);
      });
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
      const detail = err.response?.data?.detail;
      const msg = (typeof detail === 'string' && detail) || (typeof err.message === 'string' && err.message) || '未知错误';
      toast.error('发起完善失败: ' + msg);
      setRefining(false);
    }
  };

  // 挂载时恢复进行中的生成任务（页面刷新场景）
  useEffect(() => {
    // 通过本地包装触发恢复，避免在 effect 体内直接同步调用 setState（react-hooks/set-state-in-effect）
    const tick = () => {
      const savedJobId = sessionStorage.getItem(JOB_STORAGE_KEY);
      if (savedJobId) {
        setStep(3);
        subscribeJob(savedJobId);
      }
    };
    tick();
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const incoming = Array.from(list);
    setFiles((prev) => {
      const known = new Set(prev.map((f) => f.name));
      return [...prev, ...incoming.filter((f) => !known.has(f.name))];
    });
  };

  const removeFile = (fileName: string) => {
    setFiles((prev) => prev.filter((f) => f.name !== fileName));
  };

  const submit = async () => {
    if (files.length === 0 || submitting) return;
    setSubmitting(true);
    setError('');
    setResult(null);
    setDecision(null);
    setLint(null);
    setDoneEvents([]);
    setParsedCount(0);
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append('files', f));
      if (targetKind) formData.append('target_kind', targetKind);
      const config: Record<string, string> = {};
      if (name.trim()) config.name = name.trim();
      if (description.trim()) config.description = description.trim();
      if (Object.keys(config).length > 0) formData.append('config', JSON.stringify(config));

      const res = await apiClient.post('/generate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const jobId = res.data.job_id as string;
      setJobId(jobId);
      sessionStorage.setItem(JOB_STORAGE_KEY, jobId);
      setStep(3);
      subscribeJob(jobId);
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
      const detail = err.response?.data?.detail;
      const msg = (typeof detail === 'string' && detail) || (typeof err.message === 'string' && err.message) || '未知错误';
      toast.error('提交失败: ' + msg);
    } finally {
      setSubmitting(false);
    }
  };

  const download = async () => {
    if (!result?.artifact_id || downloading) return;
    setDownloading(true);
    try {
      const blob = await downloadGeneratedArtifact(result.artifact_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${result.name || 'artifact'}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      const err = e as { message?: unknown };
      toast.error(typeof err.message === 'string' && err.message ? err.message : '下载失败');
    } finally {
      setDownloading(false);
    }
  };

  const share = async () => {
    if (!result?.artifact_id || sharing) return;
    setSharing(true);
    try {
      const res = await apiClient.post('/shares', {
        resource_type: 'capability_pack',
        resource_id: result.artifact_id,
      });
      const url = `${window.location.origin}/share/${res.data.token}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success('分享链接已复制到剪贴板（24 小时内有效）');
      } catch {
        // 剪贴板不可用（非安全上下文等）时直接展示链接
        toast.info(`分享链接：${url}`);
      }
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } } };
      const detail = err.response?.data?.detail;
      toast.error('创建分享失败' + (typeof detail === 'string' ? `: ${detail}` : ''));
    } finally {
      setSharing(false);
    }
  };

  const reset = () => {
    abortRef.current?.abort();
    sessionStorage.removeItem(JOB_STORAGE_KEY);
    setStep(1);
    setFiles([]);
    setTargetKind('');
    setName('');
    setDescription('');
    setResult(null);
    setDecision(null);
    setLint(null);
    setError('');
    setDoneEvents([]);
    setParsedCount(0);
    setJobId(null);
    setRefining(false);
    setRefineInstruction('');
    setRefineDoneEvents([]);
    setRefineSummary(null);
    setRefineError('');
  };

  return (
    <div className="container">
      <h1>
        <Rocket size={22} className="inline mr-1" /> 素材工坊
      </h1>
      <p className="text-muted mb-4">上传素材，一键蒸馏为可部署的能力产物（技能 / MCP 工具 / 知识库）</p>

      {/* 素材要求说明 */}
      <div className="card mb-4" style={{ background: 'var(--bg-primary)' }}>
        <h3 style={{ marginTop: 0, marginBottom: 8, fontSize: 15 }}>
          <Info size={16} className="inline mr-1" /> 上传素材要求
        </h3>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
          <li>支持格式：txt / md / docx / pdf，单文件不超过 10MB</li>
          <li>建议内容：领域文档、FAQ、操作手册、对话记录、接口规范等</li>
          <li>案例：上传客服 FAQ 文档 → 生成客服问答技能 → 打包为可部署的 Agent 能力包</li>
        </ul>
      </div>

      {/* 步骤指示 */}
      <div className="card mb-4" style={{ padding: '12px 16px' }}>
        <div className="flex items-center gap-4" style={{ flexWrap: 'wrap' }}>
          {STEP_TITLES.map((title, i) => {
            const num = i + 1;
            const active = step === num;
            const done = step > num;
            return (
              <div key={title} className="flex items-center gap-2">
                <span
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 600,
                    background: active ? 'var(--gradient-primary)' : done ? 'var(--color-success, #22c55e)' : 'var(--bg-tertiary)',
                    color: active || done ? 'white' : 'var(--text-muted)',
                  }}
                >
                  {done ? <CheckCircle2 size={14} /> : num}
                </span>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: active ? 600 : 400,
                    color: active ? 'var(--text-primary)' : 'var(--text-muted)',
                  }}
                >
                  {title}
                </span>
                {num < STEP_TITLES.length && <ChevronRight size={14} className="text-muted" style={{ margin: '0 4px' }} />}
              </div>
            );
          })}
        </div>
      </div>

      {/* 第一步：材料上传 */}
      {step === 1 && (
        <div className="card">
          <h3>
            <Upload size={18} className="inline mr-1" /> 上传材料
          </h3>
          <p className="text-muted text-sm mt-2">
            上传需求文档、接口规范、知识资料等，系统将自动解析并推荐合适的产物类型。
          </p>
          <div className="flex flex-col gap-3 mt-4">
            <input
              type="file"
              multiple
              accept={ACCEPT}
              aria-label="选择材料文件"
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = '';
              }}
            />
            {files.length > 0 && (
              <div className="list">
                {files.map((f) => (
                  <div key={f.name} className="list-item">
                    <div className="list-item-content">
                      <div className="flex items-center gap-2">
                        <FileText size={16} className="text-primary" />
                        <strong>{f.name}</strong>
                        <span className="text-muted text-sm">({formatSize(f.size)})</span>
                      </div>
                    </div>
                    <div className="list-item-actions">
                      <button className="secondary text-xs" onClick={() => removeFile(f.name)} title="移除文件">
                        <Trash2 size={12} className="inline text-error" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div>
              <button className="primary" onClick={() => setStep(2)} disabled={files.length === 0}>
                下一步：选择产物类型
              </button>
            </div>
            <div className="text-muted text-xs">支持格式：Markdown、TXT、DOCX、PDF、JSON、YAML</div>
          </div>
        </div>
      )}

      {/* 第二步：产物类型 */}
      {step === 2 && (
        <div className="card">
          <h3>
            <Sparkles size={18} className="inline mr-1" /> 选择产物类型
          </h3>
          <p className="text-muted text-sm mt-2">默认由系统根据材料内容自动决策，也可以手动指定。</p>

          <div className="grid mt-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
            {(
              [
                { key: '', label: '自动决策', icon: Sparkles, desc: '由系统分析材料后推荐' },
                { key: 'skill', label: '技能（Skill）', icon: Zap, desc: '从流程/规范材料蒸馏技能包' },
                { key: 'mcp', label: 'MCP 工具', icon: Wrench, desc: '从 OpenAPI/Swagger 规范生成' },
                { key: 'kb', label: '知识库（KB）', icon: Database, desc: '从文档材料构建知识库' },
              ] as const
            ).map((opt) => {
              const Icon = opt.icon;
              const selected = targetKind === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => setTargetKind(opt.key)}
                  style={{
                    textAlign: 'left',
                    padding: 12,
                    borderRadius: 'var(--radius-md)',
                    border: `1px solid ${selected ? 'var(--color-primary)' : 'var(--border-default)'}`,
                    background: selected ? 'var(--bg-tertiary)' : 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <div className="flex items-center gap-2" style={{ fontWeight: 600, fontSize: 14 }}>
                    <Icon size={16} className="text-primary" />
                    {opt.label}
                  </div>
                  <div className="text-muted text-xs mt-1">{opt.desc}</div>
                </button>
              );
            })}
          </div>

          <div className="flex flex-col gap-3 mt-4">
            <div>
              <label className="text-sm text-muted" htmlFor="ml-name">
                产物名称（可选，默认取首份材料文件名）
              </label>
              <input
                id="ml-name"
                type="text"
                style={{ width: '100%', marginTop: 4 }}
                placeholder="例如：customer-service-skill"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm text-muted" htmlFor="ml-desc">
                产物描述（可选）
              </label>
              <textarea
                id="ml-desc"
                style={{ width: '100%', marginTop: 4, minHeight: 72 }}
                placeholder="一句话说明这个产物的用途"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <button className="secondary" onClick={() => setStep(1)}>
              上一步
            </button>
            <button className="primary" onClick={submit} disabled={submitting || files.length === 0}>
              {submitting ? (
                <>
                  <Loader2 size={14} className="inline animate-spin mr-1" /> 提交中...
                </>
              ) : (
                <>
                  <Rocket size={14} className="inline mr-1" /> 开始生成
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* 第三步：生成进度 */}
      {step === 3 && (
        <div className="card">
          <h3>
            <Loader2 size={18} className="inline mr-1 animate-spin" /> 生成进度
          </h3>
          <div className="list mt-4">
            {STAGES.map((stage) => {
              const done = doneEvents.includes(stage.key);
              const started = doneEvents.length > 0 || parsedCount > 0;
              const isCurrent = !done && !error && started && STAGES.findIndex((s) => !doneEvents.includes(s.key)) === STAGES.indexOf(stage);
              return (
                <div key={stage.key} className="list-item">
                  <div className="flex items-center gap-2">
                    {done ? (
                      <CheckCircle2 size={16} style={{ color: 'var(--color-success, #22c55e)' }} />
                    ) : isCurrent ? (
                      <Loader2 size={16} className="animate-spin text-primary" />
                    ) : (
                      <Circle size={16} className="text-muted" />
                    )}
                    <span style={{ color: done || isCurrent ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                      {stage.label}
                      {stage.key === 'parsed' && parsedCount > 0 && (
                        <span className="text-muted text-sm">（已解析 {parsedCount} 份材料）</span>
                      )}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {decision && (
            <div className="card mt-4" style={{ background: 'var(--bg-tertiary)' }}>
              <div className="flex items-center gap-2">
                <strong>决策结果：{kindLabel(decision.kind)}</strong>
                <span className="badge secondary text-xs">置信度 {Math.round(decision.confidence * 100)}%</span>
                {decision.override && <span className="badge secondary text-xs">用户指定</span>}
              </div>
              {decision.reasons.length > 0 && (
                <ul className="text-muted text-sm mt-2" style={{ paddingLeft: 18, margin: 0 }}>
                  {decision.reasons.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {lint && !error && (
            <div className="mt-4 text-sm">
              {lint.passed ? (
                <span className="badge success">质量检查通过</span>
              ) : (
                <span className="badge error">质量检查发现 {lint.errors.length} 个问题</span>
              )}
              {lint.warnings.length > 0 && (
                <span className="text-muted ml-2">{lint.warnings.length} 条警告</span>
              )}
            </div>
          )}

          {error && (
            <div className="card mt-4" style={{ borderColor: 'var(--color-error, #ef4444)' }}>
              <div className="flex items-center gap-2">
                <XCircle size={16} className="text-error" />
                <strong className="text-error">生成失败</strong>
              </div>
              <p className="text-muted text-sm mt-2">{error}</p>
              <button className="secondary mt-2" onClick={() => setStep(2)}>
                <RotateCcw size={14} className="inline mr-1" /> 返回重新生成
              </button>
            </div>
          )}
        </div>
      )}

      {/* 第四步：产物预览与下载 */}
      {step === 4 && result && (
        <div className="card">
          <h3>
            <CheckCircle2 size={18} className="inline mr-1" style={{ color: 'var(--color-success, #22c55e)' }} /> 产物已生成
          </h3>

          <div className="mt-4">
            <div className="flex items-center gap-2">
              <strong style={{ fontSize: 16 }}>{result.name}</strong>
              <span className="badge secondary">{kindLabel(result.kind)}</span>
              {result.degraded && (
                <span className="badge secondary text-xs" title="LLM 不可用，使用规则兜底生成">
                  降级生成
                </span>
              )}
            </div>

            {result.decision && (
              <div className="mt-4">
                <div className="text-sm text-muted mb-1">决策依据（置信度 {Math.round((result.decision.confidence || 0) * 100)}%）</div>
                <ul className="text-muted text-sm" style={{ paddingLeft: 18, margin: 0 }}>
                  {(result.decision.reasons || []).map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </div>
            )}

            {result.lint && (
              <div className="mt-4">
                <div className="text-sm text-muted mb-1">质量检查</div>
                {result.lint.passed ? (
                  <span className="badge success">通过</span>
                ) : (
                  <div>
                    <span className="badge error">{result.lint.errors.length} 个错误</span>
                    <ul className="text-error text-sm mt-2" style={{ paddingLeft: 18 }}>
                      {result.lint.errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {result.lint.warnings.length > 0 && (
                  <ul className="text-muted text-sm mt-2" style={{ paddingLeft: 18 }}>
                    {result.lint.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {result.files && result.files.length > 0 && (
              <div className="mt-4">
                <div className="text-sm text-muted mb-1">文件清单（{result.files.length} 个）</div>
                <div className="list">
                  {result.files.map((path) => (
                    <div key={path} className="list-item">
                      <div className="flex items-center gap-2">
                        <FileText size={14} className="text-primary" />
                        <code className="text-sm">{path}</code>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2 mt-4">
              <button className="primary" onClick={download} disabled={downloading}>
                {downloading ? (
                  <>
                    <Loader2 size={14} className="inline animate-spin mr-1" /> 下载中...
                  </>
                ) : (
                  <>
                    <Download size={14} className="inline mr-1" /> 下载产物 zip
                  </>
                )}
              </button>
              <button className="secondary" onClick={share} disabled={sharing}>
                {sharing ? (
                  <>
                    <Loader2 size={14} className="inline animate-spin mr-1" /> 创建中...
                  </>
                ) : (
                  <>
                    <Share2 size={14} className="inline mr-1" /> 分享
                  </>
                )}
              </button>
              <Link
                to={`/artifacts/${result.artifact_id}/edit`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 16px',
                  fontSize: 14,
                  fontWeight: 500,
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--btn-secondary-bg)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-default)',
                  textDecoration: 'none',
                }}
              >
                <Pencil size={14} className="inline" /> 打开编辑器
              </Link>
              <button className="secondary" onClick={reset}>
                <RotateCcw size={14} className="inline mr-1" /> 再次生成
              </button>
              <Link to={`/playground/${result.artifact_id}`} className="text-sm" style={{ alignSelf: 'center' }}>
                <FlaskConical size={14} className="inline mr-1" /> 去试玩
              </Link>
            </div>
          </div>

          {/* 继续完善（B16：gap 分析 → 增量补齐） */}
          <div className="card mt-4" style={{ background: 'var(--bg-tertiary)' }}>
            <div className="flex items-center gap-2">
              <Wrench size={16} className="text-primary" />
              <strong>继续完善（增量修订）</strong>
              {result.version && <span className="badge secondary text-xs">v{result.version}</span>}
            </div>
            <p className="text-muted text-sm mt-2">
              输入补充要求，对产物做一轮缺口分析 → 增量补齐（只改需要改的片段，不整篇重写）。
            </p>
            <textarea
              aria-label="完善补充要求"
              style={{ width: '100%', marginTop: 8, minHeight: 64 }}
              placeholder="例如：补充故障排查场景、增加对 XX 能力的说明…"
              value={refineInstruction}
              onChange={(e) => setRefineInstruction(e.target.value)}
            />
            <div className="flex items-center gap-2 mt-2">
              <button className="primary" onClick={refine} disabled={refining || !refineInstruction.trim()}>
                {refining ? (
                  <>
                    <Loader2 size={14} className="inline animate-spin mr-1" /> 完善中...
                  </>
                ) : (
                  <>
                    <Wrench size={14} className="inline mr-1" /> 开始完善
                  </>
                )}
              </button>
              {refineError && <span className="text-error text-sm">{refineError}</span>}
            </div>

            {refining && (
              <div className="list mt-4">
                {REFINE_STAGES.map((stage) => {
                  const done = refineDoneEvents.includes(stage.key);
                  const isCurrent =
                    !done &&
                    !refineError &&
                    refineDoneEvents.length > 0 &&
                    REFINE_STAGES.findIndex((s) => !refineDoneEvents.includes(s.key)) === REFINE_STAGES.indexOf(stage);
                  return (
                    <div key={stage.key} className="list-item">
                      <div className="flex items-center gap-2">
                        {done ? (
                          <CheckCircle2 size={16} style={{ color: 'var(--color-success, #22c55e)' }} />
                        ) : isCurrent ? (
                          <Loader2 size={16} className="animate-spin text-primary" />
                        ) : (
                          <Circle size={16} className="text-muted" />
                        )}
                        <span style={{ color: done || isCurrent ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                          {stage.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {refineSummary && (
              <div className="mt-4">
                <span className="badge success">完善完成{refineSummary.version ? `（v${refineSummary.version}）` : ''}</span>
                {refineSummary.gap_analysis && (
                  <p className="text-sm mt-2">
                    <strong>缺口分析：</strong>
                    {refineSummary.gap_analysis}
                  </p>
                )}
                <p className="text-sm text-muted mt-1">
                  已应用 {refineSummary.changes_applied ?? 0} 处修订，跳过 {refineSummary.changes_skipped ?? 0} 处
                </p>
                {(refineSummary.warnings ?? []).length > 0 && (
                  <ul className="text-muted text-sm mt-2" style={{ paddingLeft: 18, margin: 0 }}>
                    {(refineSummary.warnings ?? []).map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
